import { useDispatch, useSelector } from "react-redux";
import useAlerts from "./useAlerts";
import useLoading from "./useLoading";
import CustomerService from "../services/CustomerService";

import {
  setLoading,
  setSubmitting,
  setCustomerList,
  setCustomersDropdown,
  setCustomerDetail,
  updateCustomerStatusInList,
  updateField,
  resetCustomerForm,
  setPaginator,
  setShowRegistrationModal,
  setShowViewModal,
  setShowEditModal,
  setErrors,
  addCustomerToList,
  setCustomerFormForEdit,
  clearCustomerList,
  clearIdentificationFields,
} from "../store/slices/customer/customerSlice";

import { RootState, AppDispatch } from "../store/store";
import { ICustomerForm } from "../types/customer/customerInterfaces";
import useApiErrorHandler from "./useApiErrorHandler";

export const useCustomer = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { show: showAlert } = useAlerts();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { handle } = useApiErrorHandler();


  const {
    form,
    detail,
    list,
    paginator,
    isLoading,
    isSubmitting,
    customersDropdown,
    showRegistrationModal,
    showViewModal,
    showEditModal,
    errors,
  } = useSelector((state: RootState) => state.customers);

  /**
   * 🔍 Obtener clientes paginados
   */
  const fetchCustomersPaged = async (page = 0, size = 10) => {
    try {
      dispatch(setLoading(true));
      showLoadingIndicator();
      const response = await CustomerService.getAllPaged(page, size);
      const { statusCode, data } = response;
      if (statusCode === 200) {
        dispatch(setCustomerList(data.content));
        dispatch(setPaginator(data));
      }
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setLoading(false));
      hideLoadingIndicator();
    }
  };

  /**
 * 🔎 Carga los clientes filtrados con paginación.
 */
  const fetchFilteredCustomers = async (
    filters: any,               // puedes tipar con CustomerFilter si lo creaste
    page = 0,
    size = 10,
    sort = 'name,asc'
  ) => {
    try {
      dispatch(setLoading(true));
      showLoadingIndicator();

      // 👉 Llamamos al servicio
      const response = await CustomerService.getFiltered(filters, page, size, sort);

      // ⚠️ OJO: la API devuelve un CustomResponse, así que desestructuramos:
      // { statusCode, data, message, type, title }
      const { statusCode, data } = response;

      if (statusCode === 200) {
        // data aquí es el objeto paginado completo
        dispatch(setCustomerList(data.content));
        dispatch(setPaginator(data));
      }
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setLoading(false));
      hideLoadingIndicator();
    }
  };

  const saveCustomer = async (
    options: { showSuccess?: boolean; refreshList?: boolean } = {}
  ) => {
    const payload = form; // Datos del formulario actual
    const isUpdate = Boolean(payload.id && payload.id !== 0);

    try {
      dispatch(setSubmitting(true));
      showLoadingIndicator();

      // 📡 Llamada al servicio según si es update o create
      const response = isUpdate
        ? await CustomerService.modify(payload.id!, payload)
        : await CustomerService.add(payload);

      // 📦 Destructuramos la respuesta
      const {
        statusCode,
        type,
        title,
        message,
        data: customerResponse,
      } = response;

      // ✅ Validación de éxito
      const success = statusCode === 200 || statusCode === 201;
      if (success) {
        // Si es nuevo registro, lo agregamos a la lista
        if (!isUpdate) {
          dispatch(addCustomerToList(customerResponse));
        }

        // ✅ Mostrar alerta de éxito
        if (options.showSuccess !== false) {
          showAlert(type, title, message);
        }

        // 📌 Cerrar modal según contexto
        isUpdate ? closeEditModal() : closeAddModal();

        // 🧹 Limpiar errores previos
        dispatch(setErrors(null));
        if (options.refreshList !== false) {
          fetchCustomersPaged();
        }

        return customerResponse;
      }
      return null;
    } catch (err: any) {
      // 🚦 Si hay errores de validación, guardarlos en Redux
      if (err.response?.data?.data) {
        dispatch(setErrors(err.response.data.data));
      }
      handle(err);
      return null;
    } finally {
      // 🔄 Restaurar estados
      dispatch(setSubmitting(false));
      hideLoadingIndicator();
    }
  };


  /**
 * ✅ Obtener cliente por ID para edición
 */
  const loadCustomerForEdit = async (id: number) => {
    try {
      dispatch(setLoading(true));
      showLoadingIndicator();

      // 👉 Llamamos al nuevo servicio
      const response = await CustomerService.getCustomerForEdit(id);
      console.log(response);

      // Suponiendo que tu servicio devuelve { statusCode, data, message, type }
      const { statusCode, data } = response;

      if (statusCode === 200) {
        // 🔥 Guardamos los datos completos de edición en Redux
        dispatch(setCustomerFormForEdit(data));

        // ✅ Abrimos el modal de edición
        openEditModal();
      }
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setLoading(false));
      hideLoadingIndicator();
    }
  };





  /**
   * ✅ Obtener cliente por ID
   */
  const loadCustomerById = async (id: number) => {
    try {
      dispatch(setLoading(true));
      showLoadingIndicator();

      const response = await CustomerService.getById(id);
      console.log(response);

      const { statusCode, data } = response;

      if (statusCode === 200) {
        dispatch(setCustomerDetail(data));
        openViewModal();
      }
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setLoading(false));
      hideLoadingIndicator();
    }
  };

  /**
   * ✅ Alternar estado del cliente
   */
  const toggleStatus = async (id: number) => {
    try {
      dispatch(setSubmitting(true));
      showLoadingIndicator();

      const response = await CustomerService.toggleStatus(id);
      if (response.data?.statusCode === 200) {
        dispatch(updateCustomerStatusInList({ id, status: response.data.data.status }));
        showAlert(response.data.type, response.data.title, response.data.message);
      }
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setSubmitting(false));
      hideLoadingIndicator();
    }
  };

  /**
   * ✅ Obtener clientes para dropdown
   */
  const handlerGetCustomersForDropdown = async () => {
    try {
      const response = await CustomerService.getDropdown();
      const { statusCode, data } = response;
      if (statusCode === 200) {
        dispatch(setCustomersDropdown(data));
      }
    } catch (error) {
      handle(error);
    }
  };

  const updateFormField = <K extends keyof ICustomerForm>(field: K, value: any) => {
    dispatch(updateField({ field, value }));
  };


  const resetForm = () => {
    dispatch(resetCustomerForm());
  };

  // 👇 Control de modales
  const openAddModal = () => dispatch(setShowRegistrationModal(true));
  const closeAddModal = () => dispatch(setShowRegistrationModal(false));

  const openViewModal = () => dispatch(setShowViewModal(true));
  const closeViewModal = () => dispatch(setShowViewModal(false));

  const openEditModal = () => dispatch(setShowEditModal(true));
  const closeEditModal = () => dispatch(setShowEditModal(false));

  const changeEntityType = (newType: number) => {
    dispatch(resetCustomerForm()); // limpia todo
    dispatch(updateField({ field: 'entityTypeId', value: newType })); // setea tipo
  };


  /**
   * Lipia la lista de clientes y reseta el paginador
   */
  const handlerClearFilteredCustomers = () => {
    dispatch(clearCustomerList())
  }

  const clearIdentification = () => {
    dispatch(clearIdentificationFields());
  };


  return {
    // 📌 Estados
    form,
    detail,
    list,
    paginator,
    isLoading,
    isSubmitting,
    customersDropdown,
    showRegistrationModal,
    showViewModal,
    showEditModal,
    errors,

    // 📌 Métodos
    fetchCustomersPaged,
    fetchFilteredCustomers,
    handlerGetCustomersForDropdown,
    saveCustomer,
    loadCustomerForEdit,
    loadCustomerById,
    toggleStatus,

    updateFormField,
    resetForm,

    openAddModal,
    closeAddModal,
    openViewModal,
    closeViewModal,
    openEditModal,
    closeEditModal,

    changeEntityType,

    handlerClearFilteredCustomers,
    clearIdentification,
  };
};
